import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Lock, Globe, Check } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { usePatchRequest } from '@/shared/hooks/requests.js';
import { FILE_VISIBILITY, NODE } from '@/shared/utils/urls.js';
import { cn } from '@/lib/utils';

/**
 * A small clickable visibility badge (lock = private, globe = public) that
 * overlays a file/folder icon. Clicking opens a "who can access" popover to
 * switch between Private and Public. Files use the PATCH visibility endpoint;
 * folders use the unified PATCH node endpoint. `nodeUuid`: the file or folder.
 */
export const VisibilityControl = ({ nodeUuid, visibility, isFolder = false, onChanged, size = 14, url = null }) => {
  const { t } = useTranslation(['my-docs']);
  const [open, setOpen] = useState(false);

  // `url` overrides the endpoint: the template library patches
  // /embed/v1/templates/{template_uuid}, whose visibility rule also admits managers.
  const fileReq = usePatchRequest({ url: url || FILE_VISIBILITY.replace('{file_uuid}', nodeUuid) });
  const folderReq = usePatchRequest({ url: url || NODE.replace('{node_uuid}', nodeUuid) });
  const req = isFolder ? folderReq : fileReq;

  const isPublic = visibility === 'public';
  const Icon = isPublic ? Globe : Lock;

  const change = async (value) => {
    if (value === visibility) { setOpen(false); return; }
    const { success } = await req.request({ data: { visibility: value } });
    if (success) {
      toast.success(t('my-docs:visibility.success'));
      onChanged?.();
    }
    setOpen(false);
  };

  const Option = ({ value, optIcon, accent, bg }) => (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); change(value); }}
      disabled={req.loading}
      className={cn(
        'w-full flex items-center gap-3 rounded-md px-2 py-2 text-left transition-colors hover:bg-muted',
        visibility === value && 'bg-muted/60'
      )}
    >
      <span className={cn('p-1.5 rounded-md', bg, accent)}>{optIcon}</span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium text-foreground">{t(`my-docs:visibility.${value}`)}</span>
        <span className="block text-xs text-muted-foreground truncate">{t(`my-docs:visibility.${value}Desc`)}</span>
      </span>
      {visibility === value && <Check className="w-4 h-4 text-primary shrink-0" />}
    </button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((o) => !o); }}
          title={isPublic ? t('my-docs:visibility.public') : t('my-docs:visibility.private')}
          aria-label={t('my-docs:visibility.title')}
          className={cn(
            'flex items-center justify-center rounded-full border bg-white shadow-sm transition-all',
            'hover:scale-110 hover:shadow-md active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
            isPublic ? 'text-sky-500 border-sky-200' : 'text-rose-500 border-rose-200'
          )}
          style={{ width: size + 9, height: size + 9 }}
        >
          <Icon style={{ width: size, height: size }} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-60 p-1"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
      >
        <div className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t('my-docs:visibility.whoCanAccess')}
        </div>
        <Option value="private" optIcon={<Lock className="w-4 h-4" />} accent="text-rose-500" bg="bg-rose-50" />
        <Option value="public" optIcon={<Globe className="w-4 h-4" />} accent="text-sky-500" bg="bg-sky-50" />
      </PopoverContent>
    </Popover>
  );
};
